import { Button, Divider, Steps } from "antd";
import BusinessAreaSystemConnections from "containers/BusinessAreaSystemConnections";
import SystemConnectionAwsS3BucketContentList from "containers/SystemConnectionAwsS3BucketContentList";
import SystemConnectionAwsS3Buckets from "containers/SystemConnectionAwsS3Buckets";
import SystemConnectionHubspotObjectFields from "containers/SystemConnectionHubspotObjectFields";
import SystemConnectionHubspotObjects from "containers/SystemConnectionHubspotObjects";
import SystemConnectionJdbcTables from "containers/SystemConnectionJdbcTables";
import SystemConnectionSalesforceObjectFields from "containers/SystemConnectionSalesforceObjectFields";
import SystemConnectionSalesforceObjects from "containers/SystemConnectionSalesforceObjects";
import SystemConnectionSnowflakeTables from "containers/SystemConnectionSnowflakeTables";
import { useEffect, useState } from "react";

const DEFAULT_STEPS = [{ key: "Select System Connection", title: "Select System Connection" }];

/** @type {import('./types').ISystemConnectionSelector} */
const SystemConnectionSelector = ({
    businessAreaId,
    onConnectionSelectionComplete,
    allowedFileTypes = [],
    allowNewConnectionDestination = false,
    readSelectedDestination = true,
    importButtonLabel,
}) => {
    const [stepItems, setStepItems] = useState(DEFAULT_STEPS);
    const [current, setCurrent] = useState(0);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [stepResult, setStepResult] = useState([]);

    useEffect(() => {
        if (selectedConnection) {
            switch (selectedConnection.systemConnection.type) {
                case "aws_s3":
                    setStepItems(
                        DEFAULT_STEPS.concat([
                            {
                                key: "Select AWS S3 Bucket File",
                                title: "Select AWS S3 Bucket File",
                            },
                        ]),
                    );
                    break;
                case "jdbc":
                    setStepItems(
                        DEFAULT_STEPS.concat([
                            {
                                key: "Select JDBC Table",
                                title: "Select JDBC Table",
                            },
                        ]),
                    );
                    break;
                case "snowflake":
                    setStepItems(
                        DEFAULT_STEPS.concat([
                            {
                                key: "Select Snowflake Table",
                                title: "Select Snowflake Table",
                            },
                        ]),
                    );
                    break;
                case "salesforce":
                    setStepItems(
                        DEFAULT_STEPS.concat([
                            {
                                key: "Select Salesforce Object",
                                title: "Select Salesforce Object",
                            },
                            {
                                key: "Select Fields to Import",
                                title: "Select Fields to Import",
                            },
                        ]),
                    );
                    break;
                case "hubspot":
                    setStepItems(
                        DEFAULT_STEPS.concat([
                            {
                                key: "Select HubSpot Object",
                                title: "Select HubSpot Object",
                            },
                            {
                                key: "Select Fields to Import",
                                title: "Select Fields to Import",
                            },
                        ]),
                    );
                    break;
                default:
                    setStepResult([]);
                    setStepItems(DEFAULT_STEPS);
                    break;
            }
        }
    }, [selectedConnection]);

    const next = () => {
        if (selectedConnection) {
            if (current === 0) {
                setCurrent(1);
            } else {
                setCurrent(stepResult.length + 1);
            }
        }
    };

    const prev = () => {
        setCurrent(current - 1);
        let currentStepResult = [...stepResult];
        if (currentStepResult.length === current) {
            currentStepResult.splice(currentStepResult.length - 1, 1);
            setStepResult(currentStepResult);
        }
        onConnectionSelectionComplete(null);
    };

    const onStepResultChanged = (step, result) => {
        let currentStepResult = [...stepResult];
        if (currentStepResult.length - 1 < step) {
            currentStepResult.push(null);
        }
        if (result) {
            currentStepResult[step] = result;
        } else {
            currentStepResult.splice(currentStepResult.length - 1, 1);
        }
        setStepResult(currentStepResult);

        if (selectedConnection.systemConnection.type === "aws_s3" && currentStepResult.length === 1) {
            const { file, bucket } = currentStepResult[0];
            if (file && bucket) {
                onConnectionSelectionComplete({
                    connectionId: selectedConnection.systemConnection.connectionId,
                    connectionName: selectedConnection.systemConnection.name,
                    connectionType: "aws_s3",
                    bucket,
                    ...file,
                });
            } else {
                onConnectionSelectionComplete(null);
            }
        }
        if (selectedConnection.systemConnection.type === "jdbc" && step === 1 && result) {
            onConnectionSelectionComplete({
                connectionId: selectedConnection.systemConnection.connectionId,
                connectionType: "jdbc",
                file: {
                    name: `jdbc://${selectedConnection.systemConnection.connectionId}/${result.tableName}`,
                    connectionId: selectedConnection.systemConnection.connectionId,
                    connectionType: "jdbc",
                    tableName: result.tableName,
                },
                fileReadResult: {
                    data: result.data,
                    meta: { fields: result.columns },
                },
            });
        }
        if (selectedConnection.systemConnection.type === "snowflake" && step === 1 && result) {
            onConnectionSelectionComplete({
                connectionId: selectedConnection.systemConnection.connectionId,
                connectionType: "snowflake",
                file: {
                    name: `snowflake://${selectedConnection.systemConnection.connectionId}/${result.tableName}`,
                    connectionId: selectedConnection.systemConnection.connectionId,
                    connectionType: "snowflake",
                    tableName: result.tableName,
                },
                fileReadResult: {
                    data: result.data,
                    meta: { fields: result.columns },
                },
            });
        }
        if (selectedConnection.systemConnection.type === "salesforce" && step === 2 && result) {
            onConnectionSelectionComplete({
                connectionId: selectedConnection.systemConnection.connectionId,
                connectionType: "salesforce",
                file: {
                    name: `salesforce://${selectedConnection.systemConnection.connectionId}/${result.tableName}`,
                    connectionId: selectedConnection.systemConnection.connectionId,
                    connectionType: "salesforce",
                    tableName: result.tableName,
                },
                fileReadResult: {
                    data: result.data,
                    meta: { fields: result.columns, salesforceFields: result.fields },
                },
            });
        }
        if (selectedConnection.systemConnection.type === "hubspot" && step === 2 && result) {
            onConnectionSelectionComplete({
                connectionId: selectedConnection.systemConnection.connectionId,
                connectionType: "hubspot",
                file: {
                    name: `hubspot://${selectedConnection.systemConnection.connectionId}/${result.tableName}`,
                    connectionId: selectedConnection.systemConnection.connectionId,
                    connectionType: "hubspot",
                    tableName: result.tableName,
                },
                fileReadResult: {
                    data: result.data,
                    meta: { fields: result.columns, hubspotFields: result.fields },
                },
            });
        }
    };

    const onFileSelected = (file) => {
        if (file) {
            let currentStepResult = stepResult[0];
            if (currentStepResult.bucket) {
                onStepResultChanged(0, { ...currentStepResult, file });
            } else {
                onStepResultChanged(0, {
                    bucket: currentStepResult.Name,
                    file,
                });
            }
        } else {
            onStepResultChanged(0, { Name: stepResult[0].bucket });
        }
    };

    const getContent = (current) => {
        if (current === 0) {
            return (
                <BusinessAreaSystemConnections
                    businessAreaId={businessAreaId}
                    onConnectionSelected={(conn) => {
                        if (conn && conn.systemConnection && typeof conn.systemConnection.type === "string") {
                            setSelectedConnection({
                                ...conn,
                                systemConnection: {
                                    ...conn.systemConnection,
                                    type: conn.systemConnection.type.toLowerCase().trim(),
                                },
                            });
                            return;
                        }
                        setSelectedConnection(conn);
                    }}
                />
            );
        } else {
            switch (selectedConnection.systemConnection.type) {
                case "aws_s3":
                    if (current === 1) {
                        if (stepResult.length === 0) {
                            return (
                                <SystemConnectionAwsS3Buckets
                                    connectionId={selectedConnection.systemConnection.connectionId}
                                    onBucketSelected={(bucket) => onStepResultChanged(0, bucket)}
                                ></SystemConnectionAwsS3Buckets>
                            );
                        } else {
                            return (
                                <SystemConnectionAwsS3BucketContentList
                                    connectionId={selectedConnection.systemConnection.connectionId}
                                    bucket={stepResult[0].Name}
                                    onFileSelected={(file) => onFileSelected(file)}
                                    goToBuckets={() => onStepResultChanged(0, null)}
                                    allowNewKey={allowNewConnectionDestination}
                                    readSelectedFile={readSelectedDestination}
                                    allowedFileTypes={allowedFileTypes}
                                ></SystemConnectionAwsS3BucketContentList>
                            );
                        }
                    }
                    break;
                case "jdbc":
                    if (current === 1) {
                        return (
                            <SystemConnectionJdbcTables
                                connectionId={selectedConnection.systemConnection.connectionId}
                                onTableSelected={(preview) => {
                                    onStepResultChanged(1, preview);
                                }}
                            ></SystemConnectionJdbcTables>
                        );
                    }
                    break;
                case "snowflake":
                    if (current === 1) {
                        return (
                            <SystemConnectionSnowflakeTables
                                connectionId={selectedConnection.systemConnection.connectionId}
                                onTableSelected={(preview) => {
                                    onStepResultChanged(1, preview);
                                }}
                            ></SystemConnectionSnowflakeTables>
                        );
                    }
                    break;
                case "salesforce":
                    if (current === 1) {
                        return (
                            <SystemConnectionSalesforceObjects
                                connectionId={selectedConnection.systemConnection.connectionId}
                                onObjectSelected={(objectName) => {
                                    onStepResultChanged(1, objectName);
                                    setCurrent(2);
                                }}
                            ></SystemConnectionSalesforceObjects>
                        );
                    } else if (current === 2) {
                        return (
                            <SystemConnectionSalesforceObjectFields
                                connectionId={selectedConnection.systemConnection.connectionId}
                                objectName={stepResult[1]}
                                importButtonLabel={importButtonLabel}
                                onPreviewComplete={(preview) => {
                                    onStepResultChanged(2, preview);
                                }}
                            ></SystemConnectionSalesforceObjectFields>
                        );
                    }
                    break;
                case "hubspot":
                    if (current === 1) {
                        return (
                            <SystemConnectionHubspotObjects
                                connectionId={selectedConnection.systemConnection.connectionId}
                                onObjectSelected={(objectName) => {
                                    onStepResultChanged(1, objectName);
                                    setCurrent(2);
                                }}
                            ></SystemConnectionHubspotObjects>
                        );
                    } else if (current === 2) {
                        return (
                            <SystemConnectionHubspotObjectFields
                                connectionId={selectedConnection.systemConnection.connectionId}
                                objectName={stepResult[1]}
                                importButtonLabel={importButtonLabel}
                                onPreviewComplete={(preview) => {
                                    onStepResultChanged(2, preview);
                                }}
                            ></SystemConnectionHubspotObjectFields>
                        );
                    }
                    break;
                default:
                    return <></>;
            }
        }
    };

    return (
        <div className="flex size-full flex-col gap-3">
            <Steps current={current} size="small" items={stepItems.map((item) => ({ title: item.title }))} />
            <div className="flex-1">{getContent(current)}</div>
            <div className="flex justify-end">
                {current === 0 || current < stepItems.length - 1 ? (
                    <Button type="primary" onClick={next}>
                        Next
                    </Button>
                ) : (
                    <></>
                )}
                {current > 0 ? <Button onClick={prev}>Previous</Button> : <></>}
            </div>
            <Divider className="mb-4 mt-0" />
        </div>
    );
};

export default SystemConnectionSelector;
